import React, { useState, useRef } from "react";
import api, { apiErr, receiptUrl } from "@/lib/api";
import { Card, Btn } from "@/components/common";
import { fmtDate, money } from "@/lib/format";
import { toast } from "sonner";
import { UploadSimple, Receipt, CheckCircle, Warning, ArrowRight, Trash, LinkSimple, MagnifyingGlass, Image as ImageIcon, FilePdf, X } from "@phosphor-icons/react";
import { RECEIPT_ACCEPT, RECEIPT_HINT, hasAcceptedExtension, readingFoundNothing, READING_FOUND_NOTHING, readAmount } from "@/lib/uploads";

export function BulkReceiptMatcher({ studentId, students }) {
  const [step, setStep] = useState("upload"); // upload | matching | review | done
  const [files, setFiles] = useState([]); // { file, name, status, receiptId, draft }
  const [matches, setMatches] = useState([]); // from backend
  const [selections, setSelections] = useState({}); // receiptId -> expense_id | null
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { attached, skipped }
  const fileRef = useRef(null);

  // A queued file belongs to the beneficiary chosen when it was queued.
  // Switching beneficiaries clears the queue AND bumps this generation
  // token, which the upload loop checks before every file — otherwise the
  // in-flight loop's closure would resurrect the cleared queue and keep
  // storing the remaining files under the OLD beneficiary. (ZEN-142 family,
  // arena rounds 7–8)
  const generation = useRef(0);
  React.useEffect(() => {
    generation.current += 1;
    setFiles([]);
  }, [studentId]);

  const addFiles = (newFiles) => {
    const added = Array.from(newFiles).filter(f => {
      return hasAcceptedExtension(f.name);
    }).map(f => ({ file: f, name: f.name, status: "pending", receiptId: null, draft: null }));
    setFiles(prev => [...prev, ...added]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const uploadAndMatch = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      // Step 1: Upload each file through the one-receipt door. One receipt per
      // photo is the rule (Maryse, 3 Sep 2026, ZEN-185).
      const allReceiptIds = [];
      const updatedFiles = [...files];
      const gen = generation.current;
      for (let i = 0; i < updatedFiles.length; i++) {
        // The beneficiary changed under this loop — everything not yet
        // uploaded stays un-uploaded rather than landing under the new name.
        if (generation.current !== gen) { setBusy(false); return; }
        const f = updatedFiles[i];
        if (f.status === "uploaded") { allReceiptIds.push(f.receiptId); continue; }
        f.status = "uploading";
        setFiles([...updatedFiles]);
        try {
          const fd = new FormData();
          fd.append("file", f.file);
          if (studentId) fd.append("student_id", studentId);
          const { data } = await api.post("/receipts/upload", fd);
          f.receiptId = data.id;
          f.draft = data.draft;
          f.aiError = data.ai_error;
          f.status = "uploaded";
          allReceiptIds.push(data.id);
        } catch (e) {
          const msg = apiErr(e);
          if (msg.includes("Duplicate")) {
            f.status = "duplicate";
            f.errorMsg = msg;
          } else {
            f.status = "error";
            f.errorMsg = msg;
          }
        }
        setFiles([...updatedFiles]);
      }
      // Step 2: Bulk match
      const validIds = allReceiptIds.filter(Boolean);
      if (!validIds.length) { toast.error("No receipts uploaded successfully"); setBusy(false); return; }
      const { data: matchData } = await api.post("/receipts/bulk-match", {
        receipt_ids: validIds,
        student_id: studentId || null,
      });
      setMatches(matchData.matches || []);
      // Pre-select best matches (score >= 50 and no existing receipt)
      const sel = {};
      for (const m of (matchData.matches || [])) {
        if (m.best_match && !m.best_match.has_receipt && m.best_match.score >= 50) {
          sel[m.receipt_id] = m.best_match.expense_id;
        }
      }
      setSelections(sel);
      setStep("review");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmAttach = async () => {
    const pairs = Object.entries(selections)
      .filter(([, eid]) => eid)
      .map(([rid, eid]) => ({ receipt_id: rid, expense_id: eid }));
    if (!pairs.length) { toast.error("No matches selected"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/receipts/bulk-attach", { pairs });
      setResult(data);
      setStep("done");
      toast.success(`${data.attached.length} receipt${data.attached.length === 1 ? "" : "s"} attached`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("upload");
    setFiles([]);
    setMatches([]);
    setSelections({});
    setResult(null);
  };

  const studentName = (sid) => (students || []).find(s => s.id === sid)?.full_name || "—";

  const selectedCount = Object.values(selections).filter(Boolean).length;

  // ── Upload step ──
  if (step === "upload") return (
    <Card className="mt-6 border-[#034AA2]/30" data-testid="bulk-receipt-section">
      <div className="mb-3 flex items-center gap-2">
        <LinkSimple size={18} weight="bold" className="text-[#034AA2]" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-[#034AA2]">Attach Receipts to Existing Expenses</h3>
      </div>
      <p className="mb-3 text-xs text-[#6b7280]">
        Drop receipt photos or PDFs here — one receipt per photo. The app reads each one and matches it to an existing expense. Several paper receipts? Take a separate photo of each one. You'll review every match before anything is attached. Expenses that already have a receipt will be skipped.
      </p>
      {!studentId ? (
        // Same gate as the import dropzone above it: a receipt is somebody's
        // evidence, and this box used to upload with NO beneficiary on the
        // "All beneficiaries" view — the server now refuses that (ZEN-142),
        // and this explains it before the refusal instead of after.
        <div className="rounded-xl border-2 border-dashed border-[#D97706] bg-[#FCF5EE] p-6 text-center" data-testid="bulk-choose-student-first">
          <Warning size={34} weight="bold" className="mx-auto text-[#D97706]" />
          <div className="mt-2 text-sm font-bold text-[#92600A]">Choose a beneficiary above before uploading</div>
          <p className="mt-1 text-xs text-[#6b7280]">Receipts must be assigned to a specific beneficiary. Pick one at the top of the page, then drop files here to match them.</p>
        </div>
      ) : (
      <div
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#034AA2]", "bg-[#034AA2]/10"); }}
        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/10"); }}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/10"); addFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        role="button"
        className="cursor-pointer rounded-xl border-2 border-dashed border-[#C2C9D1] bg-[#F8FAFC] p-5 text-center transition-colors hover:bg-[#E9E9EB]/50"
        data-testid="bulk-receipt-dropzone"
      >
        <UploadSimple size={28} weight="bold" className="mx-auto text-[#034AA2]" />
        <div className="mt-1.5 text-sm font-bold text-[#1f2937]">Drop receipt files to match</div>
        <p className="mt-0.5 text-xs text-[#6b7280]">{RECEIPT_HINT} — drop as many as you need</p>
        <input ref={fileRef} type="file" accept={RECEIPT_ACCEPT} multiple className="hidden"
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-receipt-input" />
      </div>
      )}
      {files.length > 0 && (
        <div className="mt-3" data-testid="bulk-receipt-file-list">
          <div className="mb-2 text-xs font-semibold text-[#6b7280]">{files.length} file{files.length === 1 ? "" : "s"} queued</div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-[#C2C9D1] bg-white px-2.5 py-1.5" data-testid={`bulk-file-${i}`}>
                {f.name.toLowerCase().endsWith(".pdf")
                  ? <FilePdf size={16} className="shrink-0 text-[#F01919]" />
                  : <ImageIcon size={16} className="shrink-0 text-[#034AA2]" />}
                <span className="min-w-0 flex-1 truncate text-xs text-[#1f2937]">{f.name}</span>
                {f.status === "duplicate" && <span className="shrink-0 text-[10px] font-bold text-[#D97706]">DUP</span>}
                {f.status === "error" && <span className="shrink-0 text-[10px] font-bold text-[#F01919]">ERR</span>}
                {f.status === "uploaded" && <span className="shrink-0 text-[10px] font-bold text-[#077A39]">✓</span>}
                <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="shrink-0 text-[#6b7280] hover:text-[#F01919]">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Btn onClick={uploadAndMatch} disabled={busy} data-testid="bulk-receipt-match-btn">
              {busy ? (
                <><span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> Uploading & matching…</>
              ) : (
                <><MagnifyingGlass size={16} weight="bold" /> Upload & Find Matches ({files.filter(f => f.status !== "duplicate" && f.status !== "error").length})</>
              )}
            </Btn>
          </div>
        </div>
      )}
    </Card>
  );

  // ── Review step ──
  if (step === "review") return (
    <Card className="mt-6 border-[#034AA2]/30" data-testid="bulk-receipt-review">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt size={18} weight="bold" className="text-[#034AA2]" />
          <h3 className="text-sm font-bold uppercase tracking-wide text-[#034AA2]">Review Matches ({matches.length})</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6b7280]">{selectedCount} of {matches.length} matched</span>
          <Btn variant="ghost" onClick={reset} data-testid="bulk-receipt-back">← Back</Btn>
        </div>
      </div>
      <div className="space-y-3">
        {matches.map((m) => (
          <MatchRow key={m.receipt_id} m={m} selection={selections[m.receipt_id] || null}
            uploaded={files.find((f) => f.receiptId === m.receipt_id)}
            onSelect={(eid) => setSelections(prev => ({ ...prev, [m.receipt_id]: eid }))}
            studentName={studentName} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-[#C2C9D1] pt-3">
        <span className="text-xs text-[#6b7280]">
          {selectedCount} receipt{selectedCount === 1 ? "" : "s"} will be attached.
          {matches.length - selectedCount > 0 && ` ${matches.length - selectedCount} unmatched.`}
        </span>
        <Btn onClick={confirmAttach} disabled={busy || selectedCount === 0} data-testid="bulk-receipt-attach-btn">
          {busy ? "Attaching…" : <><CheckCircle size={16} weight="bold" /> Attach {selectedCount} Receipt{selectedCount === 1 ? "" : "s"}</>}
        </Btn>
      </div>
    </Card>
  );

  // ── Done step ──
  return (
    <Card className="mt-6 border-[#077A39]/30" data-testid="bulk-receipt-done">
      <div className="mb-3 flex items-center gap-2">
        <CheckCircle size={18} weight="bold" className="text-[#077A39]" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-[#077A39]">Bulk Attach Complete</h3>
      </div>
      {result?.attached?.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-xs font-semibold text-[#077A39]">✓ Attached ({result.attached.length})</div>
          {result.attached.map((a, i) => (
            <div key={i} className="text-xs text-[#374151]">Receipt → <b>{a.payee || a.expense_id?.slice(0, 8)}</b></div>
          ))}
        </div>
      )}
      {result?.skipped?.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-xs font-semibold text-[#D97706]">⚠ Skipped ({result.skipped.length})</div>
          {result.skipped.map((s, i) => (
            <div key={i} className="text-xs text-[#374151]">
              {s.payee || s.receipt_id?.slice(0, 8)}: <span className="text-[#D97706]">{s.reason}</span>
            </div>
          ))}
        </div>
      )}
      <Btn variant="ghost" onClick={reset} data-testid="bulk-receipt-restart">Attach more receipts</Btn>
    </Card>
  );
}


function MatchRow({ m, uploaded, selection, onSelect, studentName }) {
  const draft = m.draft || {};
  const best = m.best_match;
  const hasCandidates = (m.candidates || []).length > 0;
  const [expanded, setExpanded] = useState(false);
  const isSelected = !!selection;

  return (
    <div className={`rounded-lg border p-3 ${isSelected ? "border-[#077A39]/40 bg-[#077A39]/5" : "border-[#C2C9D1] bg-[#F8FAFC]"}`} data-testid={`match-row-${m.receipt_id}`}>
      <div className="flex items-start gap-3">
        {/* Receipt thumbnail */}
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#C2C9D1] bg-white">
          {m.receipt_id ? (
            <img src={receiptUrl(m.receipt_id)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <Receipt size={20} className="text-[#6b7280]" />
          )}
        </div>
        {/* Receipt OCR summary */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-[#1f2937]">{draft.payee || m.filename || "Unknown"}</span>
            <span className="text-[10px] text-[#6b7280]">{m.filename}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-[#6b7280]">
            {draft.transaction_date && fmtDate(draft.transaction_date)}
            {readAmount(draft) != null && <> · {money(Number(readAmount(draft)), draft.original_currency || "USD")}</>}
            {draft.category && <> · {draft.category}</>}
          </div>
          {readingFoundNothing({ draft: uploaded?.draft, ai_error: uploaded?.aiError }) && (
            <div className="mt-1 text-[11px] text-[#92600A]" data-testid={`reading-found-nothing-${m.receipt_id}`}>{READING_FOUND_NOTHING}</div>
          )}
          {/* Match result */}
          {best ? (
            <div className="mt-1.5">
              <div className="flex items-center gap-1 text-[11px]">
                <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${best.score >= 70 ? "bg-[#077A39]/10 text-[#077A39]" : best.score >= 40 ? "bg-[#D97706]/10 text-[#92400E]" : "bg-[#E9E9EB] text-[#6b7280]"}`}>
                  {best.score >= 70 ? "Strong match" : best.score >= 40 ? "Possible match" : "Weak match"} ({best.score}%)
                </span>
                {best.has_receipt && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-[#D97706]/10 px-2 py-0.5 text-[10px] font-bold text-[#D97706]">
                    <Warning size={10} weight="bold" /> Already has receipt
                  </span>
                )}
              </div>
              <div className="mt-1 rounded-md border border-[#C2C9D1] bg-white px-2.5 py-1.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio" name={`match-${m.receipt_id}`}
                    checked={selection === best.expense_id}
                    onChange={() => onSelect(best.expense_id)}
                    disabled={best.has_receipt}
                    className="accent-[#077A39]"
                    data-testid={`match-select-best-${m.receipt_id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#1f2937]">{best.payee || "—"}</div>
                    <div className="text-[11px] text-[#6b7280]">
                      {fmtDate(best.date)} · {money(Number(best.amount || 0), best.currency || "USD")}
                      {best.status && <> · <span className="capitalize">{best.status}</span></>}
                      {studentName && best.student_id && <> · {studentName(best.student_id)}</>}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          ) : (
            <div className="mt-1.5 text-[11px] text-[#D97706] font-semibold">No matching expense found</div>
          )}
          {/* Other candidates */}
          {hasCandidates && m.candidates.length > 1 && (
            <div className="mt-1">
              <button onClick={() => setExpanded(!expanded)} className="text-[11px] font-semibold text-[#034AA2] hover:underline" data-testid={`match-expand-${m.receipt_id}`}>
                {expanded ? "Hide" : "Show"} {m.candidates.length - 1} other candidate{m.candidates.length - 1 === 1 ? "" : "s"}
              </button>
              {expanded && (
                <div className="mt-1 space-y-1">
                  {m.candidates.slice(1).map((c) => (
                    <div key={c.expense_id} className="rounded-md border border-[#E9E9EB] bg-white px-2.5 py-1.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name={`match-${m.receipt_id}`}
                          checked={selection === c.expense_id}
                          onChange={() => onSelect(c.expense_id)}
                          disabled={c.has_receipt}
                          className="accent-[#077A39]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-semibold text-[#1f2937]">{c.payee || "—"}</span>
                            <span className={`text-[10px] ${c.score >= 70 ? "text-[#077A39]" : "text-[#6b7280]"}`}>({c.score}%)</span>
                            {c.has_receipt && <span className="text-[10px] text-[#D97706]">has receipt</span>}
                          </div>
                          <div className="text-[11px] text-[#6b7280]">
                            {fmtDate(c.date)} · {money(Number(c.amount || 0), c.currency || "USD")}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Skip option */}
          <label className="mt-1 flex items-center gap-2 cursor-pointer text-[11px] text-[#6b7280]">
            <input type="radio" name={`match-${m.receipt_id}`} checked={!selection} onChange={() => onSelect(null)} className="accent-[#6b7280]" />
            Skip — don't attach this receipt
          </label>
        </div>
      </div>
    </div>
  );
}
